import { el } from '../dom.js';
import type { I18n } from '../i18n.js';
import type { DebriefView } from '../session.js';
import { drawRadar } from '../radar.js';

const AXES = ['Utility', 'Equality', 'Equity', 'Desert', 'Rights', 'Efficiency', 'Kinship'] as const;

export interface DebriefProps {
  i18n: I18n;
  view: DebriefView;
  onReplay: () => void;
  onWithdraw: () => void;
  onClose: () => void;
}

export function DebriefScreen({ i18n, view, onReplay, onWithdraw, onClose }: DebriefProps): HTMLElement {
  const canvas = document.createElement('canvas');
  canvas.className = 'radar';
  canvas.setAttribute('aria-label', i18n.t('debrief.radar_heading'));
  // Draw after mount so the element has a layout size.
  queueMicrotask(() => drawRadar(canvas, view.radar, AXES, i18n, view.topAxes));

  const topPatternsKey = pickPatternKey(view.topAxes[0]);
  const inconsKey = pickInconsistencyKey(view.inconsistencies[0], view.inconsistencies[1]);

  const biasKey = view.roleRevealed.roleId === 'R0'
    ? 'bias.no_highlight'
    : `bias.${view.roleRevealed.roleId}`;

  const radarSection = el('section', { id: 'sec-radar', class: 'debrief-section' }, [
    el('h2', {}, i18n.t('debrief.radar_heading')),
    el('p', {}, i18n.t('debrief.radar_note')),
    canvas,
  ]);

  const leanings = el('section', { id: 'sec-leanings', class: 'debrief-section' }, [
    el('h2', {}, i18n.t('debrief.patterns_heading')),
    el('ol', { class: 'leanings-list' }, view.topAxes.map((a, i) =>
      el('li', { class: 'leaning-item' }, [
        el('span', { class: 'leaning-rank' }, String(i + 1)),
        el('span', { class: 'leaning-label' }, i18n.t(`axes.${a}.label`)),
      ]),
    )),
    topPatternsKey ? el('p', {}, i18n.t(topPatternsKey)) : null,
  ]);

  const shifts = view.inconsistencies.length > 0
    ? el('section', { id: 'sec-shifts', class: 'debrief-section' }, [
        el('h2', {}, i18n.t('debrief.inconsistencies_heading')),
        el('ul', {}, view.inconsistencies.map((a) => el('li', {}, i18n.t(`axes.${a}.label`)))),
        inconsKey ? el('p', {}, i18n.t(inconsKey)) : null,
      ])
    : null;

  const framing = el('section', { id: 'sec-framing', class: 'debrief-section' }, [
    el('h2', {}, i18n.t('debrief.bias_heading')),
    el('p', {}, i18n.t('debrief.bias_explain')),
    el('p', {}, i18n.t(biasKey)),
    view.roleRevealed.roleId !== 'R0'
      ? el('p', { class: 'hero-lede' },
          `${i18n.t(view.roleRevealed.labelKey)} — ${i18n.t(view.roleRevealed.descriptionKey)}`)
      : null,
  ]);

  const school = el('section', { id: 'sec-school', class: 'debrief-section' }, [
    el('h2', {}, i18n.t('debrief.school_heading')),
    // DR-UX-3 — soften the label: present as "one framework that resonates"
    // rather than a verdict. Framework note sits next to the label so the
    // caveat cannot be skipped.
    el('p', { style: 'font-weight:600' }, i18n.t(`schools.${view.school}`)),
    el('p', { class: 'debrief-framework-note' }, i18n.t('debrief.school_framework_note')),
    el('p', {}, i18n.t(`school.${view.school}.description`)),
    el('p', { class: 'hero-lede' }, i18n.t('debrief.school_caveat')),
  ]);

  // DR-UX-5 — psychosocial support section. Appears near the end of debrief,
  // before replay/withdrawal CTAs. No consent needed; purely informational.
  const support = el('section', { id: 'sec-support', class: 'debrief-section' }, [
    el('h2', {}, i18n.t('debrief.support_heading')),
    el('p', {}, i18n.t('debrief.support_body')),
    el('ul', { class: 'debrief-support-links' }, [
      el('li', {}, [
        el(
          'a',
          { href: 'https://mhpss.net', target: '_blank', rel: 'noopener noreferrer' },
          i18n.t('debrief.support_mhpss_link'),
        ),
      ]),
      el('li', {}, [
        el(
          'a',
          { href: 'https://findahelpline.com', target: '_blank', rel: 'noopener noreferrer' },
          i18n.t('debrief.support_findahelpline'),
        ),
      ]),
    ]),
  ]);

  const social = el('section', { id: 'sec-social', class: 'debrief-section' }, [
    el('h2', {}, i18n.t('debrief.social_heading')),
    view.socialComparison
      ? el('p', {}, i18n.t('debrief.social_template', { percent: view.socialComparison.similarPct }))
      : el('p', {}, i18n.t('debrief.social_insufficient')),
  ]);

  const timeline = view.timeline && view.timeline.length > 0
    ? el('section', { id: 'sec-timeline', class: 'debrief-section' }, [
        el('h2', {}, i18n.t('debrief.timeline_heading')),
        el('p', { class: 'hero-lede' }, i18n.t('debrief.timeline_note')),
        el('details', { class: 'timeline' }, [
          el('summary', {}, `${i18n.t('debrief.timeline_heading')} (${view.timeline.length})`),
          ...view.timeline.map((t) => {
            const choiceLabel = t.choice === 'refuse_all'
              ? i18n.t('scenario.generic.outcome.refused')
              : t.choice === 'split'
                ? i18n.t('scenario.generic.outcome.split')
                : i18n.t(`scenario.${t.scenarioId}.outcome.T1.${t.choice}`);
            return el('article', { class: 'timeline-item' }, [
              el('h3', {}, i18n.t(t.promptKey).slice(0, 140)),
              el('p', { class: 'choice-label' }, choiceLabel),
              el('dl', { class: 'horizons' }, t.horizons.flatMap((h) => [
                el('dt', {}, i18n.t(`debrief.horizon.${h.horizon}`)),
                el('dd', {}, summariseHorizon(h.values)),
              ])),
            ]);
          }),
        ]),
      ])
    : null;

  // §8 + §9 G1 + §21.1: replay перестаёт быть primary CTA. Primary — «Close»,
  // replay выделен в собственную секцию с предупреждением об интервале и
  // priming-риске. Так мы не обещаем «чистого» второго датапойнта.
  const actions = el('div', { class: 'actions' }, [
    el('div', { class: 'actions-primary' }, [
      el('button', { onClick: () => onClose() }, i18n.t('debrief.done')),
    ]),
  ]);

  const replaySection = el('section', { id: 'sec-replay', class: 'debrief-section' }, [
    el('h3', {}, i18n.t('debrief.replay_heading')),
    el('p', { class: 'hero-lede' }, i18n.t('debrief.replay_warning')),
    el('div', { class: 'actions' }, [
      el('div', { class: 'actions-secondary' }, [
        el('button', { class: 'secondary', onClick: () => onReplay() }, i18n.t('debrief.replay')),
      ]),
    ]),
  ]);

  const withdraw = el('section', { class: 'debrief-section' }, [
    el('h3', {}, i18n.t('debrief.withdraw_heading')),
    el('p', { class: 'hero-lede' }, i18n.t('debrief.withdraw_body')),
    el('div', { class: 'actions' }, [
      el('div', { class: 'actions-secondary' }, [
        el('button', { class: 'danger', onClick: () => onWithdraw() }, i18n.t('debrief.withdraw_cta')),
      ]),
    ]),
  ]);

  const mainCol = el('div', { class: 'stack-lg' }, [
    el('header', {}, [
      el('h1', {}, i18n.t('debrief.heading')),
      el('p', { class: 'hero-lede' }, i18n.t('debrief.intro')),
    ]),
    radarSection,
    leanings,
    shifts,
    framing,
    school,
    social,
    timeline,
    support,
    actions,
    replaySection,
    withdraw,
  ]);

  const toc = el('aside', { class: 'debrief-toc', 'aria-label': 'On this page' }, [
    el('p', { class: 'debrief-toc-label' }, 'On this page'),
    el('ul', {}, [
      el('li', {}, el('a', { href: '#sec-radar' }, i18n.t('debrief.radar_heading'))),
      el('li', {}, el('a', { href: '#sec-leanings' }, i18n.t('debrief.patterns_heading'))),
      view.inconsistencies.length > 0
        ? el('li', {}, el('a', { href: '#sec-shifts' }, i18n.t('debrief.inconsistencies_heading')))
        : null,
      el('li', {}, el('a', { href: '#sec-framing' }, i18n.t('debrief.bias_heading'))),
      el('li', {}, el('a', { href: '#sec-school' }, i18n.t('debrief.school_heading'))),
      el('li', {}, el('a', { href: '#sec-social' }, i18n.t('debrief.social_heading'))),
      view.timeline && view.timeline.length > 0
        ? el('li', {}, el('a', { href: '#sec-timeline' }, i18n.t('debrief.timeline_heading')))
        : null,
      el('li', {}, el('a', { href: '#sec-support' }, i18n.t('debrief.support_heading'))),
    ]),
  ]);

  return el('section', { 'aria-label': 'Debrief', class: 'debrief-layout' }, [mainCol, toc]);
}

function summariseHorizon(values: Record<string, number>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(values)) {
    if (typeof v !== 'number' || Math.abs(v) < 0.05) continue;
    const sign = v > 0 ? '+' : '';
    parts.push(`${k} ${sign}${v.toFixed(1)}`);
  }
  return parts.length ? parts.join(', ') : '—';
}

function pickPatternKey(topAxis: string | undefined): string | null {
  switch (topAxis) {
    case 'Rights': return 'patterns.rights_first';
    case 'Utility': return 'patterns.utility_first';
    case 'Efficiency': return 'patterns.efficiency_first';
    case 'Equity': return 'patterns.equity_first';
    case 'Equality': return 'patterns.equality_first';
    case 'Desert': return 'patterns.desert_first';
    case 'Kinship': return 'patterns.kinship_first';
    default: return null;
  }
}

function pickInconsistencyKey(a: string | undefined, b: string | undefined): string | null {
  if (!a) return null;
  if (a === 'Kinship' || b === 'Kinship') return 'inconsistencies.kinship_equity';
  if (a === 'Desert' || b === 'Desert') return 'inconsistencies.desert_rights';
  if (a === 'Rights' && b === 'Utility') return 'inconsistencies.scale';
  return 'inconsistencies.framing_drift';
}
